<script setup>
/* global axios */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import AModal from './AModal.vue';
import { PLAN_TYPES } from './constants';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});
const { t } = useI18n();
const base = `/api/v1/accounts/${props.accountId}/kanban`;

const plans = ref([]);
const show = ref(false);
const form = ref({});

const load = async () => {
  plans.value = (await axios.get(`${base}/health_plans`)).data.data || [];
};
const open = (p = null) => {
  form.value = p
    ? { ...p }
    : {
        name: '',
        plan_type: 'particular',
        payment_days: 30,
        requires_auth: false,
        sessions_limit: '',
        notes: '',
        is_active: true,
      };
  show.value = true;
};
const save = async () => {
  if (!form.value.name?.trim()) return;
  if (form.value.id)
    await axios.patch(`${base}/health_plans/${form.value.id}`, form.value);
  else await axios.post(`${base}/health_plans`, form.value);
  show.value = false;
  await load();
};
const remove = async p => {
  if (
    !window.confirm(t('ATENDIMENTOS.partners.deleteConfirm', { name: p.name }))
  )
    return;
  await axios.delete(`${base}/health_plans/${p.id}`);
  await load();
};
const typeLabel = type => {
  const pt = PLAN_TYPES.find(x => x.value === type);
  return pt ? t(pt.labelKey) : '';
};

onMounted(load);
</script>

<template>
  <div class="p-6">
    <div class="flex justify-end mb-4">
      <button
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
        @click="open()"
      >
        {{ t('ATENDIMENTOS.partners.newPartner') }}
      </button>
    </div>
    <div class="overflow-hidden bg-n-solid-2 border rounded-xl border-n-weak">
      <table class="min-w-full text-sm divide-y divide-n-weak">
        <thead class="bg-n-alpha-1">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.partners.columns.name') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10"
            >
              {{ t('ATENDIMENTOS.partners.columns.type') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10 text-center"
            >
              {{ t('ATENDIMENTOS.partners.columns.paymentDaysFull') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-n-slate-10 text-right"
            >
              {{ t('ATENDIMENTOS.partners.columns.actions') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-for="h in plans" :key="h.id" class="hover:bg-n-alpha-1">
            <td class="px-4 py-3 text-n-slate-12">{{ h.name }}</td>
            <td class="px-4 py-3 text-n-slate-11">
              {{ typeLabel(h.plan_type) }}
            </td>
            <td class="px-4 py-3 text-center text-n-slate-11">
              {{ h.payment_days }}
            </td>
            <td class="px-4 py-3 text-right">
              <button
                class="text-xs text-n-blue-11 hover:underline"
                @click="open(h)"
              >
                {{ t('ATENDIMENTOS.partners.edit') }}
              </button>
              <button
                class="ml-2 text-xs text-n-ruby-11 hover:underline"
                @click="remove(h)"
              >
                {{ t('ATENDIMENTOS.patients.delete') }}
              </button>
            </td>
          </tr>
          <tr v-if="!plans.length">
            <td colspan="4" class="px-4 py-8 text-center text-n-slate-10">
              {{ t('ATENDIMENTOS.partners.empty') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <AModal
      v-if="show"
      :title="
        form.id
          ? t('ATENDIMENTOS.partners.modal.editTitle')
          : t('ATENDIMENTOS.partners.modal.newTitle')
      "
      size="sm"
      @close="show = false"
    >
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.partners.modal.namefield')
        }}</label
        ><input
          v-model="form.name"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
      </div>
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('ATENDIMENTOS.partners.modal.typeField')
        }}</label
        ><select
          v-model="form.plan_type"
          class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option v-for="pt in PLAN_TYPES" :key="pt.value" :value="pt.value">
            {{ t(pt.labelKey) }}
          </option>
        </select>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.partners.modal.paymentDaysField')
          }}</label
          ><input
            v-model="form.payment_days"
            type="number"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex-1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('ATENDIMENTOS.partners.modal.sessionsLimit')
          }}</label
          ><input
            v-model="form.sessions_limit"
            type="number"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
      </div>
      <label class="flex items-center gap-2 text-sm"
        ><input v-model="form.requires_auth" type="checkbox" />
        {{ t('ATENDIMENTOS.partners.modal.requiresAuth') }}</label
      >
      <template #footer>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="show = false"
        >
          {{ t('ATENDIMENTOS.partners.modal.cancel') }}</button
        ><button
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-60"
          @click="save"
        >
          {{ t('ATENDIMENTOS.partners.modal.save') }}
        </button>
      </template>
    </AModal>
  </div>
</template>
